import type {
  ApprovalWorkflow,
  CatalogSettings,
  CreateCatalogRequest,
  CreateCatalogResponse,
} from "@/lib/api/catalog";
import { catalogApi } from "@/lib/api/catalog";

export type { ApprovalWorkflow };

export type PublicationStatus = "DRAFT" | "PENDING_REVIEW" | "RETURNED" | "REJECTED" | "PUBLISHED";

export type CatalogWorkflowAction = "draft" | "publish" | "submit-for-review" | "save-changes";

export function normalizeApprovalWorkflow(workflow?: ApprovalWorkflow | string): ApprovalWorkflow {
  if (workflow === "enabled") return "required";
  if (workflow === "optional" || workflow === "required") return workflow;
  return "disabled";
}

export function isWorkflowEnabled(workflow: ApprovalWorkflow): boolean {
  return workflow !== "disabled";
}

export function canPublishDirectly(workflow: ApprovalWorkflow): boolean {
  return workflow === "disabled" || workflow === "optional";
}

export function canSubmitForReview(workflow: ApprovalWorkflow): boolean {
  return workflow === "optional" || workflow === "required";
}

export function getApprovalWorkflow(settings?: CatalogSettings): ApprovalWorkflow {
  return normalizeApprovalWorkflow(settings?.approvalWorkflow);
}

export function isCatalogRecordReadOnly(
  status: PublicationStatus | undefined,
  options?: { isReviewer?: boolean }
): boolean {
  if (!status || status === "PUBLISHED") return false;
  if (status === "REJECTED") return true;
  if (status === "PENDING_REVIEW") return !options?.isReviewer;
  return false;
}

export function getPublicationStatusLabel(status: PublicationStatus): string {
  const labels: Record<PublicationStatus, string> = {
    DRAFT: "Draft",
    PENDING_REVIEW: "Pending review",
    RETURNED: "Returned for correction",
    REJECTED: "Rejected",
    PUBLISHED: "Published",
  };
  return labels[status];
}

export function getPublicationStatusTone(
  status: PublicationStatus
): "neutral" | "warning" | "danger" | "success" {
  switch (status) {
    case "PUBLISHED":
      return "success";
    case "RETURNED":
    case "PENDING_REVIEW":
      return "warning";
    case "REJECTED":
      return "danger";
    default:
      return "neutral";
  }
}

export async function executeCatalogWorkflowAction(params: {
  action: CatalogWorkflowAction;
  createPayload?: CreateCatalogRequest;
  biblioId?: string;
  biblioUpdate?: CreateCatalogRequest["biblio"];
  publicationStatus?: PublicationStatus;
}): Promise<{ biblioId: string; items?: CreateCatalogResponse["items"] }> {
  const { action, createPayload, biblioId, biblioUpdate, publicationStatus } = params;

  if (action === "save-changes") {
    if (!biblioId || !biblioUpdate) {
      throw new Error("Biblio id and update payload are required");
    }
    await catalogApi.updateBiblio(biblioId, biblioUpdate);
    return { biblioId };
  }

  if (biblioId) {
    const shouldSubmit =
      (action === "submit-for-review" || action === "publish") && publicationStatus !== "PUBLISHED";
    const submitsJournalAtomically = shouldSubmit && createPayload?.catalogMethod === "JOURNAL";
    if (biblioUpdate && !submitsJournalAtomically) {
      await catalogApi.updateBiblio(biblioId, biblioUpdate);
    }

    if (shouldSubmit) {
      if (!createPayload) {
        throw new Error("Create payload is required to submit a catalog record");
      }
      await catalogApi.submitBiblio(biblioId, {
        ...createPayload,
        workflowIntent: action === "submit-for-review" ? "submit-for-review" : "publish",
      });
    }

    return { biblioId };
  }

  if (!createPayload) {
    throw new Error("Create payload is required");
  }

  const result = await catalogApi.createCatalogEntry({
    ...createPayload,
    saveAsDraft: action === "draft",
    ...(action === "publish" || action === "submit-for-review" ? { workflowIntent: action } : {}),
  });

  return { biblioId: result.biblioId, items: result.items };
}
